<script>
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import RexFormInput from '@/rex-components/rex-modal-input.vue';
import BaseIcon from '@/rex-components/BaseIcon.vue';
import RexButton from '@/rex-components/rex-button.vue';
import RexSearchSelect from '@/rex-components/RexSearchSelect.vue';
import Card from '@/rex-components/rex-card.vue';
import { mapActions } from 'vuex';
import {
  INSTALL_SERVICES_MANUALLY,
  UPDATE_SERVICE_DETAIL,
} from '@/store/modules/cloud/aws/aws.constants';
import { CloudService } from '@/services/api/integration.service';
import { notificationError, notificationSucess } from '@/utils/notification';
import { isEqual, isEmpty } from 'lodash';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import RexRadioV3 from '@/rex-components/RexRadioV3.vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import RexSwitchV3 from '@/rex-components/RexSwitchV3.vue';
import { supportedDatabases } from '@/components/Cloud/services/aws/aws.services.types';

export default {
  name: 'RdsCreateEditForm',
  components: {
    RexFormInput,
    RexModalLabel,
    BaseIcon,
    RexButton,
    RexSearchSelect,
    Card,
    Toggle: RexSwitchV3,
    RexInfoLabel,
    RexRadioV3,
    RexSpinV3,
  },
  props: {
    type: { type: String, required: true },
  },
  emits: { updateAllServices: null, close: null },
  beforeMount: async function () {
    await this.getInitialData();
  },
  computed: {
    isEdit() {
      if (this.type === 'edit') return true;
      else return false;
    },
  },
  data() {
    return {
      userInputs: {
        databases: [],
        pullFrequency: '',
        global: false,
        enableMonitoring: false,
        globalPullFrequencyMins: '',
        tagsCondition: '',
        tags: [{}],
      },
      initialValue: {
        databases: [],
        pullFrequency: '',
        global: false,
        enableMonitoring: false,
        globalPullFrequencyMins: '',
        tags: [{}],
      },
      errorState: {
        isDatatypeError: false,
        isPullFrequencyValidationError: false,
        isPullFrequencyValueError: false,
      },
      isLoading: false,
      selectedDatabases: '',
      supportedDatabases: supportedDatabases,
      radioGroupOptions: [
        { value: 'AND', label: 'AND' },
        { value: 'OR', label: 'OR' },
      ],
    };
  },
  methods: {
    isEqual,
    ...mapActions('cloudState/aws', [
      INSTALL_SERVICES_MANUALLY,
      UPDATE_SERVICE_DETAIL,
    ]),
    handleMonitorToggeling(toggleValue) {
      this.userInputs.enableMonitoring = toggleValue;
    },
    handleIsGlobalToggling(toggleValue) {
      this.userInputs.global = toggleValue;
      this.userInputs.pullFrequency = this.userInputs.globalPullFrequencyMins;
    },
    arrayToObject(arr) {
      // Using Array.reduce to transform the array into an object
      return arr.reduce((obj, item) => {
        obj[item] = true; // Assign true to each item as a property in the object
        return obj;
      }, {}); // Start with an empty object
    },
    handleMultiSelect(selectedDatabases) {
      this.supportedDatabases = this.supportedDatabases?.map((database) => {
        if (database.key === selectedDatabases.key) {
          return {
            ...database,
            selected: !selectedDatabases.selected,
          };
        }

        return { ...database, selected: database.selected };
      });

      if (!this.selectedDatabases.includes(selectedDatabases.label)) {
        this.selectedDatabases = !this.selectedDatabases
          ? selectedDatabases.label
          : `${this.selectedDatabases}, ${selectedDatabases.label}`;
      } else {
        const selectedDatabasesArray = this.selectedDatabases.split(', ');
        const index = selectedDatabasesArray.indexOf(selectedDatabases.label);
        selectedDatabasesArray.splice(index, 1);
        this.selectedDatabases = selectedDatabasesArray.join(', ');
      }

      if (this.userInputs.databases.includes(selectedDatabases.key)) {
        const index = this.userInputs.databases.indexOf(selectedDatabases.key);
        this.userInputs.databases.splice(index, 1);
      } else {
        this.userInputs.databases.push(selectedDatabases.key);
      }
    },
    async updateCurrentService() {
      this.isLoading = true;
      try {
        const data = await this[UPDATE_SERVICE_DETAIL]({
          subscriptionId: this.$route.params.subscriptionId,
          serviceSlug: 'rds',
          meta: {
            configuredEngines: this.arrayToObject(this.userInputs.databases),
          },
          pullFrequencyMins:
            this.userInputs.pullFrequency ||
            this.userInputs.globalPullFrequencyMins,
          isGlobalEnabled: this.userInputs.global,
          tagsCondition: this.userInputs.tagsCondition,
          isMonitoringEnabled: this.userInputs.enableMonitoring,
          tags:
            Object.keys(this.userInputs.tags[0]).length > 0
              ? this.userInputs.tags.filter((tag) => !isEmpty(tag))
              : [],
        });

        if (data.success) {
          this.$emit(
            'updateAllServices',
            'rds',
            this.userInputs.enableMonitoring,
          );

          notificationSucess(
            this.type === 'edit'
              ? this.$t('aws.rdsServiceUpdated')
              : this.$t('aws.awsrdsConfiguredSuccessfully'),
          );
        } else {
          notificationError(data.message);
        }
      } catch (err) {
        const error = JSON.parse(JSON.stringify(err));
        notificationError(error.message);
      } finally {
        this.isLoading = false;
      }
    },
    async handleManualInstall() {
      this.isLoading = true;
      try {
        const data = await this[INSTALL_SERVICES_MANUALLY]({
          isAutoInstall: false,
          serviceSlug: 'rds',
          subscriptionId: this.$route.params.subscriptionId,
          meta: {
            configuredEngines: this.arrayToObject(this.userInputs.databases),
          },
          tags:
            Object.keys(this.userInputs.tags[0]).length > 0
              ? this.userInputs.tags.filter((tag) => !isEmpty(tag))
              : [],
          tagsCondition: this.userInputs.tagsCondition,
          pullFrequencyMins:
            this.userInputs.pullFrequency ||
            this.userInputs.globalPullFrequencyMins,
          isMonitoringEnabled: this.userInputs.enableMonitoring,
        });

        if (data.success) {
          notificationSucess(this.$t('aws.awsrdsConfiguredSuccessfully'));
          this.$emit(
            'updateAllServices',
            'rds',
            this.userInputs.enableMonitoring,
          );
        } else {
          notificationError(data.message);
        }
      } catch (err) {
        const error = JSON.parse(JSON.stringify(err));
        notificationError(error.message);
      } finally {
        this.isLoading = false;
      }
    },
    async handleSaveClick() {
      if (!this.isFormValid()) return;
      if (this.type === 'edit') {
        await this.updateCurrentService();
      } else {
        await this.handleManualInstall();
      }
    },
    isFormValid() {
      this.errorState.isDatatypeError = false;
      this.errorState.isPullFrequencyValidationError = false;
      this.errorState.isPullFrequencyValueError = false;

      if (!this.userInputs.databases.length) {
        this.errorState.isDatatypeError = true;
        return false;
      }

      if (
        isNaN(this.userInputs.pullFrequency) ||
        this.userInputs.pullFrequency?.includes?.('.') ||
        this.userInputs.pullFrequency?.includes?.('-')
      ) {
        this.errorState.isPullFrequencyValidationError = true;
        return false;
      }
      if (
        this.userInputs.pullFrequency === '0' ||
        !this.userInputs.pullFrequency
      ) {
        this.errorState.isPullFrequencyValueError = true;
        return false;
      }
      return true;
    },
    async getInitialData() {
      if (this.$props?.type === 'edit') {
        const { data } = await CloudService.getServicesInfo({
          subscriptionId: this.$route.params.subscriptionId,
          service: 'rds',
        });
        this.userInputs.pullFrequency = data.service.pullFrequencyMins;
        this.userInputs.enableMonitoring =
          data.service.isMonitoringEnabled === 1;
        this.userInputs.global =
          data.service.pullFrequencyMins ===
          data.service.globalPullFrequencyMins;
        this.userInputs.databases = Object.keys(
          data.service.meta.configuredEngines,
        );
        this.userInputs.tagsCondition = data.service.tagsCondition;
        this.userInputs.globalPullFrequencyMins =
          data.service.globalPullFrequencyMins;
        this.userInputs.tags =
          data.service.tags.length > 0
            ? [...JSON.parse(JSON.stringify(data.service.tags)), {}]
            : [{}];

        this.initialValue = {
          ...this.userInputs,
          databases: JSON.parse(JSON.stringify(this.userInputs.databases)),
          tags:
            data.service.tags.length > 0
              ? [...JSON.parse(JSON.stringify(data.service.tags)), {}]
              : [{}],
        };

        this.selectedDatabases = this.supportedDatabases
          .filter((database) =>
            this.userInputs.databases?.includes(database.key),
          )
          ?.map((database) => database.label)
          .join(', ');

        this.supportedDatabases = this.supportedDatabases?.map((database) => {
          if (this.userInputs.databases.includes(database.key)) {
            return {
              ...database,
              selected: true,
            };
          }
          return { ...database, selected: false };
        });
      } else {
        const { data } = await CloudService.getGlobalFrequency({
          subscriptionId: this.$route.params.subscriptionId,
        });
        if (data.success) {
          this.userInputs.globalPullFrequencyMins =
            data.globalPullFrequencyMins;
        }
        this.userInputs.pullFrequency = data.globalPullFrequencyMins;
        this.userInputs.enableMonitoring = true;
        this.userInputs.global = true;
        this.userInputs.tagsCondition = 'AND';
        this.selectedDatabases = '';
      }
    },
  },
};
</script>

<template>
  <div class="rds-create-edit-form">
    <RexSpinV3 :spinning="isLoading">
      <Card
        :custom-style="{
          height: '450px',
          width: '100%',
        }"
        :header-style="{
          padding: '24px 12px',
        }"
      >
        <template #card-header>
          <p class="card-header-text">
            <span v-if="isEdit">
              {{ $t('aws.awsRdsEditForm') }}
            </span>
            <span v-else>
              {{ $t('aws.awsRds') }}
            </span>
          </p>
        </template>
        <template #card-body>
          <div class="form-container">
            <div class="input-container mt-3 mb-3">
              <RexModalLabel
                :primaryLabel="$t('aws.rdsFormFields.databaseName')"
              />
              <div class="select-databases">
                <RexSearchSelect
                  multiSelect
                  :is-edit="type === 'edit'"
                  :useParentData="true"
                  :current="selectedDatabases"
                  :objectValueIndex="1"
                  :data="supportedDatabases"
                  id="select-databases"
                  @onMultiSelect="handleMultiSelect"
                />

                <RexInfoLabel
                  :isError="true"
                  v-if="errorState.isDatatypeError"
                  :info="$t('aws.rdsFormFields.errorState.noDatatypeSelected')"
                />
              </div>
            </div>

            <div class="input-container mt-3 mb-3">
              <div class="pull-frequency-container">
                <RexModalLabel
                  :primaryLabel="$t('aws.rdsFormFields.pullFrequency')"
                />

                <div class="toggle-container">
                  {{ $t('aws.rdsFormFields.global') }}
                  <Toggle
                    @onChange="handleIsGlobalToggling"
                    v-model:checked="userInputs.global"
                  />
                </div>
              </div>
              <div class="input-field-container">
                <RexFormInput
                  v-model="userInputs.pullFrequency"
                  type="text"
                  id="input-pull-frequency"
                  :disabled="userInputs.global"
                />
                <RexInfoLabel
                  :isError="true"
                  v-if="
                    userInputs.pullFrequency &&
                    errorState.isPullFrequencyValidationError &&
                    userInputs.pullFrequency !== '0'
                  "
                  :info="
                    $t(
                      'aws.createNewConfigguration.errorState.inValidPullFrequency',
                    )
                  "
                />
                <RexInfoLabel
                  :isError="true"
                  v-if="errorState.isPullFrequencyValueError"
                  :info="$t('aws.rdsFormFields.errorState.noPullFrequency')"
                />
              </div>
            </div>

            <div class="monitoring-container">
              {{ $t('aws.rdsFormFields.enableMonitoring') }}
              <Toggle
                size="small"
                @onChange="handleMonitorToggeling"
                v-model:checked="userInputs.enableMonitoring"
              />
            </div>

            <div class="tags-container py-4">
              <div class="tag-condition-radio-button">
                <RexModalLabel :primaryLabel="$t('aws.rdsFormFields.tags')" />
                <RexRadioV3
                  class="tags-condition"
                  v-model="userInputs.tagsCondition"
                  :options="radioGroupOptions"
                ></RexRadioV3>
              </div>

              <div class="tags-inputs">
                <div
                  v-for="(currentTag, index) in userInputs.tags"
                  :key="index"
                  class="tag-input-group"
                >
                  <RexFormInput
                    class="tag-input"
                    v-model="currentTag.key"
                    type="text"
                    id="input-tag-key"
                  />
                  <RexFormInput
                    class="tag-input"
                    v-model="currentTag.value"
                    type="text"
                    id="input-tag-value"
                  />
                  <RexButton
                    :styling="{
                      backgroundColor: 'rgba(220, 89, 66, 1)',
                      width: '89px',
                    }"
                    v-if="index + 1 !== userInputs.tags.length"
                    variant="primary"
                    id="remove-tag"
                    @onClick="userInputs.tags.splice(index, 1)"
                    class="tag-button"
                  >
                    <BaseIcon height="15px" width="15px" name="deleteFilled" />
                  </RexButton>
                  <RexButton
                    v-if="index + 1 === userInputs.tags.length"
                    variant="primary"
                    id="add-tag"
                    @onClick="
                      currentTag.key &&
                        currentTag.value &&
                        userInputs.tags.push({})
                    "
                    class="tag-button"
                  >
                    <BaseIcon height="15px" width="15px" name="plus" />
                  </RexButton>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #card-footer>
          <div class="card-footer">
            <button
              @click="
                $emit('close', type === 'edit' ? 'edit' : 'manualInstall')
              "
              id="cancel"
              class="cancel-button"
            >
              {{ $t('aws.createNewConfigguration.cancel') }}
            </button>
            <RexButton
              @onClick="handleSaveClick"
              id="save"
              :variant="
                isEqual(initialValue, userInputs) ? 'disabled' : 'primary'
              "
            >
              {{ $t('aws.createNewConfigguration.save') }}
            </RexButton>
          </div>
        </template>
      </Card>
    </RexSpinV3>
  </div>
</template>

<style lang="scss" scoped>
.card-header-text {
  @include flex-center-vert;
  font-size: 14px;
  margin: 0;
  padding: 10px 0;
}

.rds-create-edit-form {
  margin-top: 10px;
}

.form-container {
  @include flex-column;
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.input-container {
  @include flex-column;
  width: 100%;
  align-items: start;
}

.select-databases {
  @include flex-column;
}

.pull-frequency-container {
  @include flex-center-justify(space-between);
  width: 100%;
}

.toggle-container {
  @include flex-center-vert;
  gap: 5px;
  font-weight: bold;
}

.input-field-container {
  @include flex-column;
  width: 100%;
}

.monitoring-container {
  @include flex-center-vert;
  gap: 5px;
  font-weight: bold;
}

.tags-container {
  @include flex-column;
  width: 100%;
}

.tag-condition-radio-button {
  @include flex-center-justify(space-between);
  width: 100%;
  font-weight: bold;
}

.tags-inputs {
  @include flex-column;
  gap: 7px;
  max-height: 6rem;
}

.tag-input-group {
  @include flex-center-justify(space-between);
  width: 100%;
}

.tag-input {
  width: 100%;
}

.tag-button {
  width: 30px;
  height: 30px;
}

.card-footer {
  @include flex-center-justify(flex-end);
  gap: 1rem;
  padding: 10px;
}

.cancel-button {
  @include flex-center;
  background: none;
  border: none;
  cursor: pointer;
  color: white;
}
</style>
